import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import Grid from "@saleor/components/Grid";
import Hr from "@saleor/components/Hr";
import { AccountErrorFragment } from "@saleor/fragments/types/AccountErrorFragment";
import { commonMessages } from "@saleor/intl";
import { getFormErrors } from "@saleor/utils/errors";
import getAccountErrorMessage from "@saleor/utils/errors/account";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    content: {
      paddingTop: theme.spacing(2)
    },
    hr: {
      margin: theme.spacing(3, 0)
    },
    sectionHeader: {
      marginBottom: theme.spacing()
    }
  }),
  { name: "CustomerInfo" }
);

export interface CustomerInfoProps {
  data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    birthday: Date;
    countryArea: string;
    gender: string;
  };
  disabled: boolean;
  errors: AccountErrorFragment[];
  onChange: (event: React.ChangeEvent<any>) => void;
}

const CustomerInfo: React.FC<CustomerInfoProps> = props => {
  const { data, disabled, errors, onChange } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  const formErrors = getFormErrors(
    [
      "firstName",
      "lastName",
      "email",
      "phone",
      "birthday",
      "countryArea",
      "gender"
    ],
    errors
  );

  return (
    <Card>
      <CardTitle
        title={
          <FormattedMessage
            defaultMessage="Personal Informations"
            description="customer informations, header"
          />
        }
      />
      <CardContent className={classes.content}>
        <Typography className={classes.sectionHeader}>
          <FormattedMessage {...commonMessages.generalInformations} />
        </Typography>
        <Grid variant="uniform">
          <TextField
            disabled={disabled}
            error={!!formErrors.firstName}
            fullWidth
            helperText={getAccountErrorMessage(formErrors.firstName, intl)}
            name="firstName"
            type="text"
            label={intl.formatMessage(commonMessages.firstName)}
            value={data.firstName}
            onChange={onChange}
          />
          <TextField
            disabled={disabled}
            error={!!formErrors.lastName}
            fullWidth
            helperText={getAccountErrorMessage(formErrors.lastName, intl)}
            name="lastName"
            type="text"
            label={intl.formatMessage(commonMessages.lastName)}
            value={data.lastName}
            onChange={onChange}
          />
          <TextField
            disabled={disabled}
            error={!!formErrors.birthday}
            fullWidth
            helperText={getAccountErrorMessage(formErrors.birthday, intl)}
            name="birthday"
            type="date"
            label={intl.formatMessage(commonMessages.birthday)}
            value={data.birthday}
            onChange={onChange}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            disabled={disabled}
            error={!!formErrors.gender}
            fullWidth
            helperText={getAccountErrorMessage(formErrors.gender, intl)}
            name="gender"
            type="text"
            label={intl.formatMessage(commonMessages.gender)}
            value={data.gender}
            onChange={onChange}
          />
          <TextField
            disabled={disabled}
            error={!!formErrors.countryArea}
            fullWidth
            helperText={getAccountErrorMessage(formErrors.countryArea, intl)}
            name="countryArea"
            type="text"
            label={intl.formatMessage(commonMessages.countryArea)}
            value={data.countryArea}
            onChange={onChange}
          />
          <TextField
            disabled={disabled}
            error={!!formErrors.countryArea}
            fullWidth
            helperText={getAccountErrorMessage(formErrors.phone, intl)}
            name="phone"
            type="text"
            label={intl.formatMessage(commonMessages.phone)}
            value={data.phone}
            onChange={onChange}
          />
        </Grid>
        <Hr className={classes.hr} />
        <Typography className={classes.sectionHeader}>
          <FormattedMessage
            defaultMessage="Contact Informations"
            description="customer contact section, header"
          />
        </Typography>
        <TextField
          disabled={disabled}
          error={!!formErrors.email}
          fullWidth
          helperText={getAccountErrorMessage(formErrors.email, intl)}
          name="email"
          type="email"
          label={intl.formatMessage(commonMessages.email)}
          value={data.email}
          onChange={onChange}
        />
      </CardContent>
    </Card>
  );
};
CustomerInfo.displayName = "CustomerInfo";
export default CustomerInfo;
